import { z } from "zod";

/** Opaque identifiers. UUIDs are generated server-side; clients never mint them. */
export const Uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "uuid");
/** Public pseudonymous actor id (not an auth id). */
export const ActorId = z.string().regex(/^act_[a-z0-9]{20,32}$/, "actorId");
/**
 * Stable world plot / property id from the versioned manifest: "c-01" commercial, "r-07" residential, "v-01" civic.
 * World v5 (ADR-0041) widens the number to two or three digits ("c-100"); every earlier id keeps its two digits.
 */
export const PropertyId = z.string().regex(/^(c|r|v)-\d{2,3}$/, "propertyId");
export const BillboardId = z.string().regex(/^bb-\d{1,2}$/, "billboardId");
export const DistrictId = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/, "districtId");
/**
 * A walk-only gate between two districts (ADR-0041), e.g. "central-east" (paired with "riverside-west"). World v6
 * (ADR-0042): retired with DistrictGate (the districts are attached by road); kept so v5 data still parses.
 */
export const GateId = z.string().regex(/^[a-z][a-z0-9_-]{1,47}$/, "gateId");

/** ISO-8601 UTC timestamp with explicit Z offset. */
export const IsoUtc = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?Z$/, "isoUtc");

/** Integer money. Internal arithmetic may use BigInt; DTOs are bounded safe integers. */
export const MAX_CENTS = 1_000_000_000_000; // $10B cap per field; well inside Number.MAX_SAFE_INTEGER
export const Cents = z.number().int().min(0).max(MAX_CENTS);
export const Coins = z.number().int().min(0).max(1_000_000_000);
export const LedgerUnit = z.enum(["CREDIT_USD", "COIN"]);
export type LedgerUnit = z.infer<typeof LedgerUnit>;

export const CountryCode = z.string().regex(/^[A-Z]{2}$/, "countryCode");

export const finiteNumber = (min: number, max: number) =>
  z
    .number()
    .refine((n) => Number.isFinite(n), { message: "must be finite" })
    .min(min)
    .max(max);

export const CursorPage = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
